// Shared math + writes for finance invoices, lender incentives, and finance
// deposits. Three surfaces create this data — the Finance tab's invoice modal,
// its "Add Incentive" modal, and the global "Add Lender Incentives" bulk tool —
// so the totalling and the Firestore shape live here to keep them from drifting.
//
// Invoice shape (`invoices` collection, type: 'finance'):
//   conciergeFee      number  — the standard fee, almost always DEFAULT_CONCIERGE_FEE
//   participationFee  number  — legacy single bank participation amount
//   incentives        array   — [{ id, memo, amount, date }] lender incentive lines
//   adjustments       number  — signed adjustment (may be negative)
//   total / revenue   number  — both equal the sum; `revenue` is what the Deal
//                               Dashboard's Finance column reads
//
// `incentives` is additive: invoices written before it existed simply have no
// array, and every reader below treats that as an empty list.

import { v4 as uuidv4 } from 'uuid';
import moment from 'moment';
import firebase from './firebase';

export const DEFAULT_CONCIERGE_FEE = 995;

// The memo marker that flags a deposit as an internal transfer rather than money
// arriving from outside. Accounting excludes these from the deposits report and
// the dashboards sum them separately, so the string matters.
export const TRANSFER_MARKER = 'TFD';

const num = (v) => {
  const n = parseFloat(v);
  return isNaN(n) ? 0 : n;
};

export const today = () => moment().format('YYYY/MM/DD');

export const getIncentives = (invoice = {}) =>
  Array.isArray(invoice.incentives) ? invoice.incentives : [];

export const incentivesTotal = (invoice = {}) =>
  getIncentives(invoice).reduce((a, c) => a + num(c.amount), 0);

// Adjustments are added, not subtracted — enter a negative number to discount.
// (The legacy `car-deposits`-era form in store.js subtracted them; the newer
// AddInvoiceDialog adds them. Adding is the behavior that survived.)
export const financeInvoiceTotal = (invoice = {}) =>
  num(invoice.conciergeFee) +
  num(invoice.participationFee) +
  incentivesTotal(invoice) +
  num(invoice.adjustments);

// Flattened rows for display: the fixed fees, then one row per incentive.
export const financeLineItems = (invoice = {}) => {
  const rows = [];
  if (num(invoice.conciergeFee)) rows.push({ key: 'conciergeFee', label: 'Concierge Fee', amount: num(invoice.conciergeFee) });
  if (num(invoice.participationFee)) rows.push({ key: 'participationFee', label: 'Bank Participation Fee', amount: num(invoice.participationFee) });
  getIncentives(invoice).forEach((inc, i) => {
    rows.push({
      key: inc.id || `incentive-${i}`,
      label: inc.memo || 'Lender Incentive',
      amount: num(inc.amount),
      isIncentive: true,
    });
  });
  if (num(invoice.adjustments)) rows.push({ key: 'adjustments', label: 'Adjustments', amount: num(invoice.adjustments) });
  return rows;
};

export const makeIncentive = ({ memo, amount, date }) => ({
  id: uuidv4(),
  memo: (memo || '').trim(),
  amount: num(amount),
  date: date || today(),
});

// Normalize a form/partial invoice into the document we persist. Always
// recomputes total (and revenue, which mirrors it for finance) so a stale total
// can never be written.
export const buildFinanceInvoice = ({ id, stockNumber, invoice = {} }) => {
  const doc = {
    id: id || uuidv4(),
    stock: stockNumber,
    type: 'finance',
    date: invoice.date || today(),
    conciergeFee: num(invoice.conciergeFee),
    participationFee: num(invoice.participationFee),
    adjustments: num(invoice.adjustments),
    incentives: getIncentives(invoice).map(inc => ({
      id: inc.id || uuidv4(),
      memo: (inc.memo || '').trim(),
      amount: num(inc.amount),
      date: inc.date || today(),
    })),
  };
  const total = financeInvoiceTotal(doc);
  doc.total = total;
  // The Deal Dashboard's Finance column reads `invoice.revenue`. For a finance
  // invoice every dollar is revenue, so the two are the same number.
  doc.revenue = total;
  return doc;
};

// Write the invoice and point the deal at it. Returns the saved document.
export const saveFinanceInvoice = async ({ id, stockNumber, invoice, select = true }) => {
  const doc = buildFinanceInvoice({ id, stockNumber, invoice });
  await firebase.firestore().collection('invoices').doc(doc.id).set(doc);
  if (select) {
    await firebase.firestore().doc('deals/' + stockNumber).set({ finance: doc.id }, { merge: true });
  }
  return doc;
};

// Fetch the finance invoice a car's deal currently points at, if any.
export const fetchActiveFinanceInvoice = async (stockNumber) => {
  const db = firebase.firestore();
  const dealSnap = await db.doc('deals/' + stockNumber).get();
  const financeId = (dealSnap.data() || {}).finance;
  if (!financeId) return null;
  const invSnap = await db.doc('invoices/' + financeId).get();
  if (!invSnap.exists) return null;
  const data = invSnap.data() || {};
  if (data.type && data.type !== 'finance') return null;
  return { id: invSnap.id, ...data };
};

// Create a finance deposit. When `transferFromDeal` is set, the memo is tagged
// with TRANSFER_MARKER and an offsetting already-paid purchase is booked against
// the same stock number, so the money shows as leaving the sales side of the deal
// and arriving on the finance side. This mirrors the `car-deposits` form callback
// in store.js — the historical source of these pairs.
export const createFinanceDeposit = async ({
  stockNumber,
  amount,
  memo,
  account,
  date,
  transferFromDeal = false,
  files,
}) => {
  const db = firebase.firestore();
  const when = date || today();
  const value = num(amount);
  const depositMemo = transferFromDeal && !(memo || '').includes(TRANSFER_MARKER)
    ? `${TRANSFER_MARKER} - transfer from sales${memo ? ` (${memo})` : ''}`
    : (memo || '');

  const ref = db.collection('deposits').doc();
  const deposit = {
    d: ref.id,
    stock: stockNumber,
    type: 'finance',
    account: account || '',
    date: when,
    amount: value,
    memo: depositMemo,
    ...(files ? { files } : {}),
  };
  await ref.set(deposit);

  if (transferFromDeal) {
    const purchaseRef = db.collection('purchases').doc();
    await purchaseRef.set({
      e: purchaseRef.id,
      stock: stockNumber,
      date: when,
      paidDate: when,
      vendor: 'Skyway Classics',
      amount: value,
      memo: `${TRANSFER_MARKER} - transfer to finance`,
      isPayable: false,
      assigned: false,
    });
  }

  return { id: ref.id, ...deposit };
};

// One lender incentive, end to end: append it to the car's finance invoice
// (creating the invoice with the standard concierge fee if the car doesn't have
// one yet) and book the matching deposit. Used per-row by the bulk tool and by
// the Finance tab's "Add Incentive" action.
export const applyIncentive = async ({ stockNumber, memo, amount, account, date, existingInvoice }) => {
  const invoice = existingInvoice !== undefined
    ? existingInvoice
    : await fetchActiveFinanceInvoice(stockNumber);

  const incentive = makeIncentive({ memo, amount, date });
  const base = invoice || { conciergeFee: DEFAULT_CONCIERGE_FEE };
  const saved = await saveFinanceInvoice({
    id: invoice?.id,
    stockNumber,
    invoice: { ...base, incentives: [...getIncentives(base), incentive] },
  });

  const deposit = await createFinanceDeposit({
    stockNumber,
    amount: incentive.amount,
    memo: incentive.memo,
    account,
    date: incentive.date,
  });

  return { invoice: saved, deposit, incentive, createdInvoice: !invoice };
};
